// Module 11 — makeDashboards: the injectable factory behind mountDashboards(). Builds the WORKSPACE — a
// dockable, collapsible, persisted set of panels (controls · live charts · SoC / load-status · object
// inspector · legend) — all fed from the state store via subscribe + shallow-diff (charts advance once
// per NEW snapshot, and only while their panel is OPEN). The returned unmount tears down the
// subscription + every panel instance (uPlot + DOM). NON-CONTRACT seam (behind frozen mountDashboards).
import type { Snapshot, FlowSample } from "../domain/index";
import type { Store, UiState } from "../state/index";
import { selProsumers, selLoads, selSelectionDetail } from "../state/index";
import type { UiDocument, UiElement, UPlotCtor, UPlotLike, ScenarioCatalog, UiStorage } from "./seam";
import { CHART_DEFS } from "./series";
import { makeLiveChart, pushChartSample } from "./charts/live-chart";
import {
  prosumerSocRows, loadStatusCounts, selectionView, formatSoc, formatLoadCounts, systemTotalsLine,
  selectionActions, deriveLogEvents, INITIAL_EVENT_LOG_STATE, type LogEvent,
} from "./panels";
import { inspectorSections, inspectorSeries, connectionRows, nodeDiagramSpec, renderSldSvg, facilityBuses } from "./inspector";
import { renderSupplyCurveSvg } from "./supply-curve";
import type { InspectorModel, InspectorSeriesSpec } from "./inspector";
import type { Command } from "../domain/index";
import { asNodeId } from "../domain/index";
import { makeControls } from "./controls";
import { makeControlPanel, makeScenarioPanel } from "./control-panel";
import { buildLegend } from "./legend";
import { buildHelp } from "./help";
import { makeWorkspace } from "./workspace";
import type { PanelDef, DockCorner } from "./workspace";

export interface DashboardsDeps {
  store: Store<UiState>;
  uplot: UPlotCtor;
  doc: UiDocument;
  catalog: ScenarioCatalog;
  // OPTIONAL (non-contract): the static reduced model — enables the full object inspector
  // (every v3 attribute + the per-object live mini-graph). Absent ⇒ the lean selection view.
  model?: InspectorModel;
  // OPTIONAL: persistence for the workspace layout (which panels are open/collapsed). Absent ⇒ in-memory.
  storage?: UiStorage;
  // Phase 1.1: optional viz callback for the voltage overlay toggle.
  // The composition root injects viz._debug.setVoltageOverlay when available.
  voltageOverlayCallback?: (on: boolean) => void;
  // OPTIONAL (P-MOBILE-V2, non-contract): true on a detected phone — only Controls + Generation boot open
  // (System totals is pinned — always visible regardless); every other panel boots closed as a compact
  // tab row (index.html's `html.gta-mobile-tabs` CSS keeps a closed panel's header visible + tappable
  // instead of `display:none`) stacked, in normal document flow, inside the one bottom drawer (#workspace).
  // Absent/false ⇒ unchanged desktop per-panel defaults.
  mobileTabMode?: boolean;
  // OPTIONAL (P-DESKTOP-FLOAT, non-contract): fired once, synchronously, right after the workspace mounts
  // — hands the composition root a narrow handle onto the live workspace instance so a REAL pointer-drag
  // handler (app/draggable-panels.ts) can move/raise floating panels. Deliberately narrower than the full
  // `Workspace` type (just the two methods dragging needs) so this doesn't become a back door for the app
  // layer to reach into open/close/etc. — those still only happen through the launcher/chip/title clicks
  // workspace.ts itself wires. A side-channel callback (not a return-value change) so `compose.ts`'s
  // narrow `(root)=>()=>void` mountDashboards contract needs zero changes.
  onWorkspaceReady?: (handle: { setPanelPosition(id: string, x: number, y: number): void; bringToFront(id: string): void }) => void;
}

export function makeDashboards(deps: DashboardsDeps): (root: UiElement) => () => void {
  const { store, uplot, doc, catalog, model } = deps;

  return (root: UiElement) => {
    const panels: PanelDef[] = [];

    // launcher groups (P14b): a few labelled clusters instead of ~18 flat chips.
    const G_CONTROL = "Control", G_CHARTS = "Charts", G_TELEM = "Telemetry", G_INSPECT = "Inspect";
    // Wave 4b — the "power-grid-monitor" chart IA: the flat 24-chart pile is split into named clusters so the
    // launcher reads as a control-room, not a wall of chips. A chart with no entry falls back to G_CHARTS.
    const G_SD = "Supply & Demand", G_ECON = "Economics", G_FREQ = "Frequency & Stability",
      G_VOLT = "Voltage", G_SEC = "Security & Reserves", G_LOSS = "Losses",
      G_STOR = "Storage & Hydro", G_PROT = "Protection & Comms", G_DIAG = "Diagnostics";
    const CHART_GROUP: Record<string, string> = {
      consumption: G_SD, generation: G_SD, balance: G_SD, roles: G_SD, intertie: G_SD, unitcommitment: G_SD,
      lambda: G_ECON, prices: G_ECON, emissions: G_ECON, settlement: G_ECON, nodalspread: G_ECON, uccost: G_ECON,
      frequency: G_FREQ, rocof: G_FREQ, inertia: G_FREQ, ace: G_FREQ, primaryreserve: G_FREQ, ufls: G_FREQ,
      voltage: G_VOLT, voltagesag: G_VOLT, voltagehist: G_VOLT,
      securitymargin: G_SEC, loadstatus: G_SEC, congestion: G_SEC, n1insecure: G_SEC, worstloading: G_SEC,
      losses: G_LOSS, heatderates: G_LOSS,
      bessoc: G_STOR, hydroreservoir: G_STOR,
      protectionstatus: G_PROT, commshealth: G_PROT,
      physicsresidual: G_DIAG,
    };

    // ── Controls (play/pause · speed · layers · env) — scenarios are their OWN panel below ─────────
    panels.push({
      id: "controls", title: "Controls", dock: "tl", defaultOpen: true, group: G_CONTROL,
      help: "Play/pause, clock speed, layer toggles, and the hour/temperature/season controls that drive demand.",
      build(host) {
        const cp = makeControlPanel(doc, { ...makeControls(store, catalog), ...(deps.voltageOverlayCallback ? { setVoltageOverlay: deps.voltageOverlayCallback } : {}) }, catalog, { scenarios: false });
        host.append(cp.el);
        return { update: (s) => cp.sync(s) };
      },
    });

    // ── Scenarios (base-anchor picker + preset event bundles) — own panel so the list is never clipped ─
    panels.push({
      id: "scenarios", title: "Scenarios", dock: "tl", defaultOpen: false, group: G_CONTROL, float: true,
      help: "Jump to a base operating point (winter peak, summer heatwave…) or inject an event (nuclear trip, cyberattack, feeder fault + FLISR).",
      build(host) {
        const sp = makeScenarioPanel(doc, { ...makeControls(store, catalog), ...(deps.voltageOverlayCallback ? { setVoltageOverlay: deps.voltageOverlayCallback } : {}) }, catalog);
        host.append(sp.el);
        return { update: (s) => sp.sync(s) }; // highlight the live base anchor
      },
    });

    // ── System totals (PINNED headline chrome — always visible, no chip, no close) ─────────────────
    panels.push({
      id: "totals", title: "System totals", dock: "tl", defaultOpen: true, pinned: true,
      help: "The always-on headline: gross demand, served MW, generation mix, imports, marginal price λ, reserve and any unserved load.",
      build(host) {
        const line = doc.createElement("div");
        line.className = "totals-line";
        host.append(line);
        return { update: (s) => { line.textContent = systemTotalsLine(s.latest); } };
      },
    });

    // ── Live charts (one dock panel each; advance once per NEW snapshot, only while open) ──
    // energy/physical charts dock bottom-left, economics/status charts bottom-right. Only Generation
    // opens by default (keeps the map clear); the user opens the rest from the launcher as needed.
    const CHART_DOCK: Record<string, { dock: DockCorner; open: boolean }> = {
      generation: { dock: "bl", open: true },
      consumption: { dock: "bl", open: false },
      roles: { dock: "bl", open: false },
      intertie: { dock: "bl", open: false },
      losses: { dock: "bl", open: false },
      balance: { dock: "br", open: false },
      lambda: { dock: "br", open: true }, // P13: the system marginal price λ open by default (demand D legible)
      prices: { dock: "br", open: false },
      emissions: { dock: "br", open: false },
      loadstatus: { dock: "br", open: false },
      settlement: { dock: "br", open: false },
      // P-CHARTS (2026-07-06 pt.2): P0-priority engine data with zero prior visibility (voltage/UC/
      // reservoir are physical/engine state → bl, alongside generation/consumption; security margin is
      // THE headline reliability number every real control room watches → br, open by default like λ).
      voltage: { dock: "bl", open: false },
      voltagesag: { dock: "bl", open: false },
      unitcommitment: { dock: "bl", open: false },
      hydroreservoir: { dock: "bl", open: false },
      securitymargin: { dock: "br", open: true },
      protectionstatus: { dock: "br", open: false },
    };
    // P-DESKTOP-FLOAT (2026-07-06 pt.5): every chart floats EXCEPT Generation/Consumption — the user's
    // explicit exception list (launcher, Controls, "the production/consumption charts" stay fixed;
    // everything else becomes an independently draggable window).
    for (const def of CHART_DEFS) {
      const place = CHART_DOCK[def.key] ?? { dock: "bl" as DockCorner, open: false };
      const isFixedChart = def.key === "generation" || def.key === "consumption";
      panels.push({
        id: `chart-${def.key}`, title: def.title, dock: place.dock, defaultOpen: place.open, group: CHART_GROUP[def.key] ?? G_CHARTS,
        ...(def.subtitle ? { help: def.subtitle } : {}), // Wave 2: surface the chart's subtitle as a chip/title tooltip
        ...(isFixedChart ? {} : { float: true }),
        build(host) {
          const el = doc.createElement("div");
          el.className = `chart chart-${def.key}`;
          host.append(el);
          // The data buffer is owned HERE and filled every tick (even while the panel is closed), so the
          // chart shows its FULL history the instant it opens — no more blank charts. The uPlot is built
          // lazily on first OPEN (a uPlot built inside a display:none panel measures 0×0), seeded with the
          // accumulated buffer, then redrawn each tick while open.
          const xs: number[] = [];
          const ys: number[][] = def.seriesLabels.map(() => []);
          let chart: ReturnType<typeof makeLiveChart> | null = null;
          let last: Snapshot | undefined;
          let lastScenario = "";
          return {
            update(s, open) {
              if (s.latest && s.latest !== last) {
                last = s.latest;
                // a scenario switch starts a fresh history — never splice the old run onto the new one
                const sid = String(s.latest.scenario.id);
                if (sid !== lastScenario) { lastScenario = sid; xs.length = 0; for (const c of ys) c.length = 0; }
                pushChartSample(def, s.latest, xs, ys);
              }
              if (open) {
                if (!chart) chart = makeLiveChart(uplot, el, def, xs, ys);
                chart.redraw();
              }
            },
            destroy() { chart?.destroy(); },
          };
        },
      });
    }

    // ── Prosumer SoC + Load-status text panels ─────────────────────────────────────
    panels.push({
      id: "soc", title: "Prosumer batteries", dock: "tr", defaultOpen: false, group: G_STOR, float: true,
      help: "Neighbourhood prosumer battery state-of-charge — charging overnight/off-peak, discharging behind-the-meter at the evening peak.",
      build(host) {
        const d = doc.createElement("div");
        host.append(d);
        return { update: (s) => { d.textContent = formatSoc(prosumerSocRows(selProsumers(s))) || "no prosumer telemetry yet"; } };
      },
    });
    panels.push({
      id: "loads", title: "Load status", dock: "tr", defaultOpen: false, group: G_SEC, float: true,
      help: "How the 449 neighbourhoods are being served right now: normal / stressed (high loading) / shed / dark.",
      build(host) {
        const d = doc.createElement("div");
        host.append(d);
        return { update: (s) => { d.textContent = formatLoadCounts(loadStatusCounts(selLoads(s))) || "no load telemetry yet"; } };
      },
    });

    // ── P13: delivered-cost SUPPLY CURVE — the "cost of supplying from different plants" diagram (demand D).
    // Reads the cost-sorted Snapshot.meritStack and draws the merit-order stack + a λ line at the marginal
    // plant; a remote plant sits higher than a near one of equal bare cost. Pure SVG via the innerHTML seam.
    panels.push({
      id: "supply", title: "Supply curve ($/MWh delivered)", dock: "br", defaultOpen: false, group: G_ECON, float: true,
      help: "The merit order: every plant stacked cheapest-first by DELIVERED cost, with the λ line at the marginal (price-setting) unit. A remote plant sits higher than a near one of equal bare cost.",
      build(host) {
        const d = doc.createElement("div"); d.className = "supply-curve-host";
        host.append(d);
        let lastSvg = " "; // sentinel so the first paint (incl. the empty placeholder) always writes
        return {
          update(s: UiState, open: boolean) {
            if (!open) return; // SVG only while visible (cheap, but match the chart-panel discipline)
            const ms = s.latest?.meritStack ?? [];
            const svg = renderSupplyCurveSvg(ms);
            if (svg === lastSvg) return;
            lastSvg = svg;
            if (svg && d.innerHTML !== undefined) d.innerHTML = svg;
            else d.textContent = svg ? "" : "Run the sim to see the dispatch supply curve.";
          },
        };
      },
    });

    // ── Object inspector (auto-opens on selection; closing it deselects + releases its mini-graph) ──
    // persist:false — it MIRRORS the (transient) selection, so a remembered "open" would restore
    // open-but-empty on the next boot. releaseInspectorChart() frees the per-object uPlot the moment the
    // panel closes (otherwise the dormant instance lingers until unmount).
    let releaseInspectorChart = (): void => { /* set in build */ };
    panels.push({
      id: "inspector", title: "Inspector", dock: "tr", defaultOpen: false, persist: false, group: G_INSPECT, fixedRight: true,
      help: "Click any bus, line, plant or neighbourhood on the map to inspect it — its live flow/voltage/dispatch, a single-line diagram, its connections (with per-connection breakers), and its bill.",
      onClose: () => { store.getState().select(undefined); releaseInspectorChart(); },
      build(host) {
        const specsEl = doc.createElement("div"); specsEl.className = "sel-specs";
        const sldEl = doc.createElement("div"); sldEl.className = "sel-sld";
        const connEl = doc.createElement("div"); connEl.className = "conn-wrap";
        const chartHost = doc.createElement("div"); chartHost.className = "sel-chart";
        const actionsEl = doc.createElement("div"); actionsEl.className = "sel-actions";
        host.replaceChildren(specsEl, sldEl, connEl, chartHost, actionsEl);

        let actionKey = "";
        let chartKey = "";
        let lastSldSvg = ""; // the single-line-diagram SVG string; re-injected only when it changes
        // CONNECTED-EDGES table refs: the structural rows are rebuilt only when the connected SET changes
        // (keyed by node + edge ids); the per-tick live values (flow, loading, breaker state) refresh in
        // place. `cmd` is re-pointed each tick so a breaker click always sends the CURRENT open/closed flip.
        let connKey = "";
        const connRows = new Map<string, { flow: UiElement; brk: UiElement; cmd: Command }>();
        let selChart: { u: UPlotLike; xs: number[]; ys: number[][]; spec: InspectorSeriesSpec } | null = null;
        let chartTick = -1;
        let lastInspScenario = "";

        const update = (s: UiState, open: boolean): void => {
          if (!open) return; // selection-driven; never build the mini-graph while hidden (0×0 trap)
          // a scenario switch starts a fresh mini-graph history (don't splice runs together)
          const sid = s.latest ? String(s.latest.scenario.id) : lastInspScenario;
          if (sid !== lastInspScenario) {
            lastInspScenario = sid;
            if (selChart) { selChart.xs.length = 0; for (const c of selChart.ys) c.length = 0; }
            chartTick = -1;
          }
          const sel = s.selection;
          // 1) specs: full attribute sections (live values refresh every tick — pure text, no churn)
          const sections = inspectorSections(sel, model, s.latest);
          if (sections.length > 0) {
            specsEl.textContent = sections
              .map((sec) => `${sec.title}\n${sec.rows.map(([k, v]) => `   ${k}: ${v}`).join("\n")}`)
              .join("\n\n");
          } else {
            const view = selectionView(selSelectionDetail(s));
            specsEl.textContent = view ? view.lines.join(" · ") : "Click a node, cable, or flow on the map to inspect it.";
          }
          // 1a) SINGLE-LINE DIAGRAM — the per-node-type schematic (live: breakers · flow arrows · energization).
          // A cheap pure SVG string; re-inject only when it changes to avoid DOM thrash. Empty for an edge/
          // packet selection or deselect. Guards on the optional `innerHTML` seam field (graceful on a fake host).
          const sldSpec = nodeDiagramSpec(sel, model, s.latest);
          const svg = sldSpec ? renderSldSvg(sldSpec) : "";
          if (svg !== lastSldSvg) {
            lastSldSvg = svg;
            if (sldEl.innerHTML !== undefined) sldEl.innerHTML = svg;
          }
          // 1b) CONNECTIONS table — for a NODE, every line on its bus + a per-connection BREAKER (the V3
          // connections_map gold standard, in-app). Click a row → inspect that line; click its breaker →
          // open/close just this station's end of it (togglePort). Structure rebuilds only when the set of
          // connected edges changes; flow/loading/breaker glyph refresh in place each tick.
          const conns = connectionRows(sel, model, s.latest);
          const cKey = sel && sel.kind === "node" ? `${sel.id}|${conns.map((c) => c.edgeId).join(",")}` : "";
          if (cKey !== connKey) {
            connKey = cKey;
            connRows.clear();
            if (conns.length === 0) {
              connEl.replaceChildren();
            } else {
              const head = doc.createElement("div");
              head.className = "conn-head";
              head.textContent = `Connections (${conns.length}) — click a line to inspect · ⏻ toggles this station's breaker`;
              const list = doc.createElement("div");
              list.className = "conn-list";
              const els: UiElement[] = [];
              for (const c of conns) {
                const row = doc.createElement("div");
                row.className = "conn-row";
                // line body (selects the edge's full datasheet) — static identity set once
                const body = doc.createElement("button");
                body.className = "conn-body";
                const l1 = doc.createElement("div"); l1.className = "conn-l1";
                const kvb = doc.createElement("span"); kvb.className = `conn-kv kv-${c.vTier}`;
                kvb.textContent = c.kv >= 1 ? `${Math.round(c.kv)}kV` : "—";
                const to = doc.createElement("span"); to.className = "conn-to";
                to.textContent = `→ ${c.toName}${c.isTie ? " · tie" : ""}`;
                l1.append(kvb, to);
                const l2 = doc.createElement("div"); l2.className = "conn-l2";
                const meta = doc.createElement("span"); meta.className = "conn-meta";
                meta.textContent = `${c.lengthKm.toFixed(1)}km · ${c.rateMva.toFixed(0)}MVA`;
                const flow = doc.createElement("span"); flow.className = "conn-flow"; // refreshed in place
                l2.append(meta, flow);
                body.append(l1, l2);
                body.addEventListener("click", () => store.getState().select({ kind: "edge", id: c.edgeId }));
                // per-connection breaker (this station's end) — cmd is re-pointed each tick so the flip is live
                const brk = doc.createElement("button");
                brk.className = "conn-brk";
                const refs = { flow, brk, cmd: c.command };
                // brk is a SIBLING of body (not nested) — its click never reaches the body's select handler
                brk.addEventListener("click", () => store.getState().dispatchCommand(refs.cmd));
                row.append(body, brk);
                els.push(row);
                connRows.set(c.edgeId, refs);
              }
              list.replaceChildren(...els);
              connEl.replaceChildren(head, list);
            }
          }
          // live refresh (flow direction/magnitude, loading, breaker glyph + the live toggle command)
          for (const c of conns) {
            const refs = connRows.get(c.edgeId);
            if (!refs) continue;
            refs.cmd = c.command;
            refs.flow.textContent = c.manualOff
              ? "OUT OF SERVICE"
              : c.faulted
                ? "⚠ FAULTED"
                : !c.inService
                  ? "idle"
                  : `${c.flowMw >= 0 ? "▶" : "◀"} ${Math.abs(c.flowMw).toFixed(0)}MW · ${c.loadingPct.toFixed(0)}%`;
            refs.flow.className = `conn-flow${c.loadingPct > 100 ? " over" : !c.inService ? " idle" : ""}`;
            refs.brk.textContent = c.open ? "⭘" : "⏻";
            refs.brk.className = `conn-brk ${c.open ? "open" : "closed"}`;
            refs.brk.setAttribute?.(
              "title",
              c.open
                ? "Disconnected at this station — click to reconnect this line"
                : "Connected — click to open this station's breaker on this line",
            );
          }
          // 2) per-object live mini-graph: (re)create only when the selected object changes
          const spec = inspectorSeries(sel, model);
          const key = sel && spec ? `${sel.kind}:${sel.id}|${spec.labels.join(",")}` : "";
          if (key !== chartKey) {
            chartKey = key;
            selChart?.u.destroy();
            selChart = null;
            chartHost.replaceChildren();
            chartTick = -1;
            if (spec) {
              const xs: number[] = [];
              const ys: number[][] = spec.labels.map(() => []);
              // Wave 6 (M2): FIT the host (the ~360px right rail on desktop, or the min(46vw,340px) drawer on a
              // phone) instead of a hardcoded 300px that overflowed a narrow mobile drawer and forced horizontal
              // scroll on every inspect. Measure the mounted host + clamp; fall back to 280 in a headless/0-width
              // context (tests). getBoundingClientRect is absent on the seam fake ⇒ the fallback path is used.
              const hostW = (chartHost as unknown as { getBoundingClientRect?: () => { width: number } }).getBoundingClientRect?.().width ?? 0;
              const w = Math.round(Math.max(220, Math.min(440, hostW > 40 ? hostW - 10 : 280)));
              const u = new uplot(
                {
                  width: w, height: 96,
                  series: [
                    { label: "tick" },
                    ...spec.labels.map((label, i) => ({ label, stroke: i === 0 ? "#ff8c1a" : "#5ec8ff", width: 2 })),
                  ],
                  scales: { x: { time: false } },
                },
                [xs, ...ys],
                chartHost,
              );
              selChart = { u, xs, ys, spec };
            }
          }
          if (selChart && s.latest && s.latest.tick !== chartTick) {
            chartTick = s.latest.tick;
            const vals = selChart.spec.values(s.latest);
            selChart.xs.push(s.latest.tick);
            for (let i = 0; i < selChart.ys.length; i++) selChart.ys[i]!.push(vals[i] ?? 0);
            if (selChart.xs.length > 120) { selChart.xs.shift(); for (const col of selChart.ys) col.shift(); }
            selChart.u.setData([selChart.xs, ...selChart.ys]);
          }
          // 3) actions: stable buttons — rebuilt only when the labels change
          const actions = selectionActions(sel, s.latest);
          const aKey = actions.map((a) => a.label).join("·");
          if (aKey !== actionKey) {
            actionKey = aKey;
            const buttons: UiElement[] = [];
            for (const action of actions) {
              const cmd = action.command;
              // P4: a command-less action is an informational, non-clickable label (e.g. static infrastructure)
              if (cmd === undefined) {
                const note = doc.createElement("span");
                note.className = "sel-action sel-action-static";
                note.textContent = action.label;
                buttons.push(note);
                continue;
              }
              const btn = doc.createElement("button");
              btn.className = "sel-action";
              btn.textContent = action.label;
              btn.addEventListener("click", () => {
                const st = store.getState();
                // A node ON/OFF fans out to the WHOLE FACILITY — every bus of the station (its _LV### winding
                // family) + the attached generator site buses (reached by a gen_interconnect lead). So turning
                // off a substation de-energizes the whole station and turning off a plant shuts its units —
                // not just the one clicked bus (the user's "the off button has no effect on many substations/
                // plants"). A single-bus facility / non-node pick falls through to the plain command.
                if (cmd.t === "toggleNode") {
                  const buses = facilityBuses(st.selection, model);
                  if (buses.length > 1) {
                    for (const id of buses) st.dispatchCommand({ t: "toggleNode", id: asNodeId(id), on: cmd.on });
                    return;
                  }
                }
                st.dispatchCommand(cmd);
              });
              buttons.push(btn);
            }
            actionsEl.replaceChildren(...buttons);
          }
        };
        releaseInspectorChart = () => {
          selChart?.u.destroy();
          selChart = null;
          chartKey = "";
          chartTick = -1;
          chartHost.replaceChildren();
        };
        return { update, destroy: () => { selChart?.u.destroy(); selChart = null; } };
      },
    });

    // ── Packets (info + finance packages between nodes) — click a row to open its datasheet ─────
    panels.push({
      id: "packages", title: "Packets (info + money)", dock: "br", defaultOpen: false, group: G_TELEM, float: true,
      help: "Recent information packets and settlement payments moving between nodes — click a row to open its datasheet.",
      build(host) {
        const hint = doc.createElement("div");
        hint.className = "pkg-hint";
        hint.textContent = "Live info + money packets flowing between nodes. Click one to open its datasheet.";
        const list = doc.createElement("div");
        list.className = "pkg-list";
        host.replaceChildren(hint, list);
        const buttons = new Map<string, UiElement>();
        let setKey = "";
        const label = (f: FlowSample): string => {
          const tag = f.layer === "finance" ? `$ ${f.kind ?? "commodity"}` : "ⓘ info";
          const amt = f.layer === "finance" ? `$${f.magnitude.toFixed(2)}` : `${f.magnitude.toFixed(1)} pkt`;
          return `${tag}  ·  ${String(f.edgeId)}  ·  ${amt}`;
        };
        // rows are keyed by LAYER + edgeId: an info packet and a finance packet on the SAME node pair
        // share an edgeId, so keying by edgeId alone would collapse them into one (perpetually-stale) row.
        const rowKey = (f: FlowSample): string => `${f.layer}|${String(f.edgeId)}`;
        return {
          update(s, open) {
            if (!open) return; // only rebuild the list while visible
            const flows = s.latest?.flows ?? [];
            const top = [...flows].sort((a, b) => b.magnitude - a.magnitude).slice(0, 14);
            const key = top.map(rowKey).join(",");
            if (key !== setKey) {
              // the displayed SET changed — rebuild the rows (stable buttons avoid click races)
              setKey = key;
              buttons.clear();
              const els: UiElement[] = [];
              if (top.length === 0) {
                const empty = doc.createElement("div");
                empty.textContent = "no packets flowing this tick";
                els.push(empty);
              }
              for (const f of top) {
                const id = String(f.edgeId);
                const btn = doc.createElement("button");
                btn.className = "pkg-row";
                btn.textContent = label(f);
                btn.addEventListener("click", () => store.getState().select({ kind: "edge", id }));
                buttons.set(rowKey(f), btn);
                els.push(btn);
              }
              list.replaceChildren(...els);
            } else {
              // same set — just refresh the live amounts in place (no structural churn)
              for (const f of top) { const b = buttons.get(rowKey(f)); if (b) b.textContent = label(f); }
            }
          },
        };
      },
    });

    // ── Event log (Phase 3.5, P-viz): a narrated "what happened" timeline — the map shows CURRENT
    // state, this shows the SEQUENCE (a fault appears → UFLS sheds → the fault clears → shed restores).
    // Accumulates regardless of open state (same discipline as the chart panels: history builds up while
    // closed, so opening the panel later shows the full narrative, not just "from now on").
    const MAX_LOG_EVENTS = 80;
    panels.push({
      id: "eventlog", title: "Event log", dock: "br", defaultOpen: false, group: G_TELEM, float: true,
      help: "A running narration of grid events: outages, trips, load shed, FLISR self-healing, UFLS, and recovery.",
      build(host) {
        const hint = doc.createElement("div");
        hint.className = "pkg-hint";
        hint.textContent = "Narrated timeline — faults, UFLS shed, island splits/merges, unserved-demand spikes.";
        const list = doc.createElement("div");
        list.className = "pkg-list";
        const empty = doc.createElement("div");
        empty.textContent = "No events yet — the grid is running normally.";
        host.replaceChildren(hint, list);
        let logState = INITIAL_EVENT_LOG_STATE;
        const events: LogEvent[] = []; // newest-first
        let last: Snapshot | undefined;
        let renderedCount = -1; // events.length as of the last DOM render — skip rebuild when unchanged
        return {
          update(s, open) {
            if (s.latest && s.latest !== last) {
              last = s.latest;
              const { next, events: fresh } = deriveLogEvents(logState, s.latest);
              logState = next;
              if (fresh.length > 0) {
                events.unshift(...fresh); // fresh is already oldest→newest within this tick; unshift keeps that order at the front
                if (events.length > MAX_LOG_EVENTS) events.length = MAX_LOG_EVENTS;
              }
            }
            if (!open || events.length === renderedCount) return; // only render while visible + on real change
            renderedCount = events.length;
            if (events.length === 0) { list.replaceChildren(empty); return; }
            const rows = events.map((e) => {
              const row = doc.createElement("div");
              row.className = `pkg-row log-row log-${e.kind}`;
              row.textContent = `tick ${e.tick} — ${e.text}`;
              return row;
            });
            list.replaceChildren(...rows);
          },
        };
      },
    });

    // ── Legend ─────────────────────────────────────────────────────────────────────
    panels.push({
      id: "legend", title: "Legend", dock: "bl", defaultOpen: false, group: G_INSPECT, float: true,
      help: "What the map colours, line widths and flow arrows mean — voltage tiers, loading heat, and the three overlay layers.",
      build(host) { buildLegend(doc, host); },
    });

    // ── Help / About — the simulator's built-in guide (Wave 3) ─────────────────────────
    panels.push({
      id: "help", title: "Help / Guide", dock: "tl", defaultOpen: false, persist: false, group: G_INSPECT, float: true,
      help: "How the whole simulator works: driving it, the three layers, reading the map, the charts, and inspecting objects.",
      build(host) { const d = doc.createElement("div"); d.className = "help-panel"; host.append(d); buildHelp(doc, d); },
    });

    // ── P-MOBILE-V2 (2026-07-07 pt.7): on a detected phone, only Controls + Generation start open (System
    // totals is pinned — always visible regardless). Everything else boots as a closed tab row inside the
    // bottom drawer (see index.html's new drawer CSS) — matches the user's explicit default-open list.
    const MOBILE_DEFAULT_OPEN_IDS = new Set(["controls", "chart-generation"]);
    const effectivePanels = deps.mobileTabMode
      ? panels.map((p) => (p.pinned || MOBILE_DEFAULT_OPEN_IDS.has(p.id) ? p : { ...p, defaultOpen: false }))
      : panels;

    // ── assemble the workspace ──────────────────────────────────────────────────────
    const workspace = makeWorkspace({
      doc, panels: effectivePanels,
      ...(deps.storage ? { storage: deps.storage } : {}),
    });
    workspace.mount(root);
    workspace.update(store.getState());
    deps.onWorkspaceReady?.(workspace);

    // Wave 3: FIRST-RUN onboarding — pop the Help guide (+ the Legend on desktop) ONCE so a first-time visitor
    // isn't dropped into a dense UI with no explanation. A 'seen' flag in the storage seam suppresses it forever
    // after; a headless boot (no storage) never triggers it, so tests/golden are unaffected.
    const HELP_SEEN_KEY = "gta:help.seen.v1";
    if (deps.storage && !deps.storage.getItem(HELP_SEEN_KEY)) {
      workspace.open("help");
      if (!deps.mobileTabMode) workspace.open("legend"); // desktop also pops the map key; keep the phone uncluttered
      try { deps.storage.setItem(HELP_SEEN_KEY, "1"); } catch { /* private mode / quota — just show again next time */ }
    }

    // the inspector panel MIRRORS selection: opens when a new object is picked, closes when deselected
    // (its ✕ also deselects via onClose — so the two stay in sync without looping, since applyOpen and
    // select(undefined) are both no-ops once already closed / already cleared).
    const selKeyOf = (s: UiState): string => (s.selection ? `${s.selection.kind}:${s.selection.id}` : "");
    let lastSelKey = selKeyOf(store.getState());
    const unsubscribe = store.subscribe((s) => {
      const selKey = selKeyOf(s);
      if (selKey !== lastSelKey) {
        if (selKey) workspace.open("inspector");
        else workspace.close("inspector");
      }
      lastSelKey = selKey;
      workspace.update(s);
    });

    return () => {
      unsubscribe();
      workspace.destroy();
      root.replaceChildren();
    };
  };
}
